"use client";

import { Section } from "@/components/section";
import { Button } from "@/components/ui/button";
import {
    Card,
    CardContent,
    CardDescription,
    CardFooter,
    CardHeader,
    CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { triggerHaptic } from "@/lib/haptic-feedback";
import type { Data } from "@/types/data";
import { FileSearch } from "lucide-react";
import { useState } from "react";

interface ResultsSectionProps {
	data: Data;
}

export default function ResultsSection({ data }: ResultsSectionProps) {
    const laboratorioId = data.cliente.cnpj.replace(/\D/g, "").replace(/^0+/, "");
	const [tipoAcesso, setTipoAcesso] = useState<"P" | "O">("P");
	const [usuario, setUsuario] = useState("");
	const [senha, setSenha] = useState("");

	const sanitize = (value: string) => value.replace(/[<>"'&]/g, "").trim();

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		triggerHaptic("medium");

		const sanitizedUsuario = sanitize(usuario);
		const sanitizedSenha = sanitize(senha);

		if (!sanitizedUsuario || !sanitizedSenha) {
			triggerHaptic("error");
			alert("Preencha todos os campos corretamente.");
			return;
		}

		const urlApi = "https://api.wiselab.com.br/v1/weblaudos/login-externo";
		const urlWeblaudos = "https://weblaudos.wiselab.com.br";
		const resultsData = { laboratorioId, tipoAcesso, usuario: sanitizedUsuario, senha: sanitizedSenha };

		try {
			const response = await fetch(urlApi, {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify(resultsData),
			});

			if (response.status === 400) {
				triggerHaptic("error");
				alert("Erro ao tentar acessar o sistema. Parâmetros inválidos.");
				return;
			}

			if (response.ok) {
				const result = await response.json();

				if (result.result) {
					triggerHaptic("success");
					window.location.href =
						tipoAcesso === "P"
							? `${urlWeblaudos}/laudo/${result.tokenId}`
							: `${urlWeblaudos}/protocolos/${result.tokenId}`;
					return;
				}

				triggerHaptic("error");
				alert(result.message);
			}
		} catch (err) {
			triggerHaptic("error");
			alert("Erro inesperado. Tente novamente mais tarde.");
			console.error(err);
		}
	};

	return (
		<Section id="resultados" className="bg-zinc-100">
			<div className="mx-auto mb-10 max-w-2xl text-center md:mb-12">
				<div className="mb-5 flex justify-center">
					<div className="rounded-full border border-brand-100 bg-brand-50 p-3">
						<FileSearch className="h-10 w-10 text-brand-secondary-500 md:h-11 md:w-11" />
					</div>
				</div>
				<h2 className="text-3xl font-bold tracking-tight text-brand-700 md:text-4xl">
					{data["area-resultados"].titulo}
				</h2>
				<p className="mx-auto mt-4 max-w-xl text-zinc-600">
					{data["area-resultados"]["texto-1"]}
				</p>
			</div>

			<Card className="relative mx-auto max-w-xl overflow-hidden rounded-2xl border-zinc-200 shadow-[0_24px_48px_-32px_rgba(24,24,27,0.45)]">
				<div className="h-1 w-full bg-brand-500" />

				<CardHeader>
					<CardTitle className="text-zinc-900">
						Acesse seus resultados
					</CardTitle>
					<CardDescription className="text-zinc-600">
						Informe os dados fornecidos pelo laboratório para consultar seus
						documentos.
					</CardDescription>
				</CardHeader>

				<form onSubmit={handleSubmit} className="grid gap-3">
					<CardContent className="grid gap-6">
						<div className="rounded-xl border border-zinc-200 bg-zinc-50 p-4">
							<Label className="mb-3 block text-zinc-800">Tipo de acesso</Label>
							<RadioGroup
								defaultValue="P"
								onValueChange={(v) => setTipoAcesso(v as "P" | "O")}
								className="flex flex-wrap gap-5"
							>
								<div className="flex items-center gap-2">
									<RadioGroupItem value="P" id="paciente" />
									<Label
										htmlFor="paciente"
										className="cursor-pointer text-zinc-700"
									>
										Paciente
									</Label>
								</div>
								<div className="flex items-center gap-2">
									<RadioGroupItem value="O" id="outros" />
									<Label
										htmlFor="outros"
										className="cursor-pointer text-zinc-700"
									>
										Outros
									</Label>
								</div>
							</RadioGroup>
						</div>

						<div className="space-y-1.5">
							<Label htmlFor="usuario" className="text-zinc-800">
								{tipoAcesso === "P" ? "Protocolo" : "Usuário"}
							</Label>
							<Input
								id="usuario"
								value={usuario}
								onChange={(e) => setUsuario(e.target.value)}
								placeholder={
									tipoAcesso === "P"
										? "Digite seu protocolo"
										: "Digite seu usuário"
								}
								required
							/>
						</div>

						<div className="space-y-1.5">
							<Label htmlFor="senha" className="text-zinc-800">
								{tipoAcesso === "P" ? "Cod. Acesso" : "Senha"}
							</Label>
							<Input
								id="senha"
								type={tipoAcesso === "P" ? "text" : "password"}
								value={senha}
								onChange={(e) => setSenha(e.target.value)}
								placeholder={
									tipoAcesso === "P"
										? "Digite o código de acesso"
										: "Digite sua senha"
								}
								required
							/>
						</div>
					</CardContent>

					<CardFooter>
						<Button type="submit" className="w-full sm:w-auto">
							Acessar agora
						</Button>
					</CardFooter>
				</form>
			</Card>
		</Section>
	);
}
